import { Button } from '@round/whitelabel-ui';
import { Modal } from '@round/whitelabel-ui';
import styles from './CreateReportModal.module.css';
import ReportForm, { FormValues, validationSchema } from '../components/ReportForm/ReportForm';
import { FormikProvider, useFormik } from 'formik';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { useCreateReportMutation } from '@round/creatorbase-helpers/api-hooks';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import { showNotification } from 'helpers';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (report: creatorbase.Report) => void;
    initialValues?: Partial<FormValues>;
};

const CreateReportModal = ({ isOpen, onClose, onCreated, initialValues }: Props) => {
    const { create } = useCreateReportMutation();
    const formik = useFormik<FormValues>({
        initialValues: {
            name: '',
            description: '',
            team: null,
            currency: null,
            assignees: [],
            project_ids: [],
            image: null,
            ...initialValues,
        },
        onSubmit: async (values, helpers) => {
            try {
                const report = await create({
                    name: values.name,
                    description: values.description,
                    team_id: values.team?.value!,
                    currency_id: values.currency?.value!,
                    assignee_ids: values.assignees?.map((a) => a.value) ?? [],
                    project_ids: values.project_ids,
                    ...(values.image instanceof File ? { image: values.image } : {}),
                    campaign_ids: [],
                });
                showNotification('Report created', 'info');
                helpers.resetForm();
                onCreated(report);
                onClose();
            } catch (e) {
                if (
                    e instanceof Error &&
                    creatorbaseQueries.reports.utils.create.isExpectedErrorResponse(e) &&
                    e.response.status === 400
                ) {
                    helpers.setErrors(
                        mapApiErrorsToFormikErrors({
                            name: e.response.data.name,
                            description: e.response.data.description,
                            team: e.response.data.team_id,
                            currency: e.response.data.currency_id,
                            assignees: e.response.data.assignee_ids,
                            project_ids: e.response.data.project_ids,
                            image: e.response.data.image,
                        })
                    );
                    return;
                }
                showNotification('Could not create report', 'error');
            }
        },
        validationSchema: validationSchema,
    });

    const handleClose = () => {
        if (formik.isSubmitting) {
            return;
        }

        formik.resetForm();
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            closeOnEscape={!formik.isSubmitting}
            closeOnOverlayClick={!formik.isSubmitting}
            className={styles.modal}
        >
            <Modal.Header>
                <Modal.Title>Create a Report</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <FormikProvider value={formik}>
                    <ReportForm />
                </FormikProvider>
            </Modal.Body>

            <Modal.Actions>
                <Button
                    appearance="primary"
                    isLoading={formik.isSubmitting}
                    disabled={formik.isSubmitting || !formik.isValid}
                    onClick={formik.submitForm}
                >
                    Create
                </Button>

                <Button onClick={handleClose} appearance="ghost" disabled={formik.isSubmitting}>
                    Cancel
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default CreateReportModal;
